import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import './EqualizerGraphs.css'

const D3ScatterPlots = ({
  data,
  mapeY,
  biasY,
  deltaY,
  dispY,
  handleClickMaterialGraph,
  inverseLogisticP
}) => {
  const containerRefMAPE = useRef()
  const containerRefBias = useRef()
  const containerRefDelta = useRef()
  const containerRefDisponibilidad = useRef()
  const [uniqueAboveIndicator, setUniqueAboveIndicator] = useState(new Set())

  useEffect(() => {
    if (data && data.length) {
      setUniqueAboveIndicator(new Set())
      drawScatterPlot(
        containerRefMAPE.current,
        data,
        'Mape',
        'porc_mape',
        'steelblue',
        inverseLogisticP(mapeY),
      )
      drawScatterPlot(
        containerRefBias.current,
        data,
        'Bias',
        'porc_bias',
        'green',
        inverseLogisticP(biasY),
      )
      drawScatterPlot(
        containerRefDelta.current,
        data,
        'Delta Costos',
        'porc_cambio_costo',
        'red',
        inverseLogisticP(deltaY),
      )
      drawScatterPlot(
        containerRefDisponibilidad.current,
        data,
        'Agotados',
        'porc_disponibilidad',
        'orange',
        inverseLogisticP(dispY),
      )
    }
  }, [data, mapeY, biasY, deltaY, dispY])

  const drawScatterPlot = (container, data, title, key, color, yLineValue) => {
    const width = 570
    const height = 300
    const margin = { top: 30, right: 20, bottom: 30, left: 70 }

    d3.select(container).selectAll('*').remove()

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    data.sort((a, b) => a.id - b.id)

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.id))
      .range([0, width - margin.left - margin.right])
      .padding(0.1)

    const getAbsoluteValue = (value) => Math.abs(value || 0)

    const yDomain = d3.extent(data, (d) => getAbsoluteValue(d[key]))
    yDomain[0] = Math.min(yDomain[0], 0, yLineValue)
    yDomain[1] = Math.max(yDomain[1], 0, yLineValue)

    const y = d3
      .scaleLinear()
      .domain(yDomain)
      .range([height - margin.top - margin.bottom, 0])
      .nice()

    g.append('g')
      .attr('transform', `translate(0,${y(0)})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(
            x.domain().filter((d, i) => i % Math.ceil(data.length / 10) === 0),
          ),
      )

      const formatYAxis = (d) => `${(d * 100).toFixed(0)}%`

      // Aplicar el formato personalizado al eje Y
      g.append('g').call(d3.axisLeft(y).tickFormat(formatYAxis))

    g.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width - margin.left - margin.right)
      .attr('height', y(yLineValue))
      .attr('fill', color)
      .attr('opacity', 0.1)

    g.append('rect')
      .attr('x', 0)
      .attr('y', y(yLineValue))
      .attr('width', width - margin.left - margin.right)
      .attr('height', height - margin.top - margin.bottom - y(yLineValue))
      .attr('fill', 'gray')
      .attr('opacity', 0.1)

    const tooltip = d3
      .select(container)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip')
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '1px')
      .style('border-radius', '5px')
      .style('padding', '10px')
      .style('position', 'absolute')

    let aboveLine = 0
    let belowLine = 0

    const drawTriangle = (x, y, size) =>
      d3.symbol().type(d3.symbolTriangle).size(size)()

    g.selectAll('path')
      .data(data)
      .enter()
      .append('path')
      .attr('transform', (d) => {
        const xPos = x(d.id) + x.bandwidth() / 2
        const yPos = y(getAbsoluteValue(d[key]))
        return `translate(${xPos}, ${yPos})`
      })
      .attr('d', (d) => {
        const value = d[key] || 0
        if (value < 0) {
          return drawTriangle(0, 0, 25)
        }
        return d3.symbol().type(d3.symbolCircle).size(10)()
      })
      .attr('fill', (d) => {
        const value = getAbsoluteValue(d[key])
        if (value >= yLineValue) {
          aboveLine++
          setUniqueAboveIndicator(prev => new Set([...prev, d.id]))
          return color
        }
        belowLine++
        return 'gray'
      })
      .on('click', (event, d) => handleClickMaterialGraph(d.id))
      .on('mouseover', function (event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', (d) => {
            if ((d[key] || 0) < 0) {
              return drawTriangle(0, 0, 100)
            }
            return d3.symbol().type(d3.symbolCircle).size(50)()
          })
        tooltip.transition().duration(200).style('opacity', 0.9)
        tooltip
          .html(`${d.id}<br/>${d.texto}<br/>Valor: ${d[key]}`)
          .style('left', `${event.pageX}px`)
          .style('top', `${event.pageY - 28}px`)
      })
      .on('mouseout', function (d) {
        d3.select(this)
          .transition()
          .duration(500)
          .attr('d', (d) => {
            if ((d[key] || 0) < 0) {
              return drawTriangle(0, 0, 25)
            }
            return d3.symbol().type(d3.symbolCircle).size(10)()
          })
        tooltip.transition().duration(500).style('opacity', 0)
      })

    g.append('line')
      .attr('x1', 0)
      .attr('y1', y(yLineValue))
      .attr('x2', width - margin.left - margin.right)
      .attr('y2', y(yLineValue))
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('opacity', 0.5)

    g.append('text')
      .attr('x', width - margin.left - margin.right)
      .attr('y', y(yLineValue) - 10)
      .attr('text-anchor', 'end')
      .attr('font-size', '12px')
      .attr('fill', color)
      .text(`+${aboveLine}`)

    g.append('text')
      .attr('x', width - margin.left - margin.right)
      .attr('y', y(yLineValue) + 20)
      .attr('text-anchor', 'end')
      .attr('font-size', '12px')
      .attr('fill', 'grey')
      .text(`-${belowLine}`)

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text(title)
  }

  return (
    <>
      <p className='obligatorios-text'>Obligatorios: {uniqueAboveIndicator.size}</p>

    <div className="equalizerGraphs-container">
      <div
        ref={containerRefMAPE}
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
      />
      <div
        ref={containerRefBias}
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
      />
      <div
        ref={containerRefDelta}
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
      />
      <div
        ref={containerRefDisponibilidad}
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
      />
    </div>
    </>
  )
}

export default D3ScatterPlots